import React, { FormEvent, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { PlusIcon, XIcon } from "@heroicons/react/solid";
import { ExternalLinkIcon } from "@heroicons/react/outline";
import { KeyboardCommandKey } from "@mui/icons-material";
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import "react-toastify/dist/ReactToastify.min.css";
import { useAuth } from "../../../contexts/AuthContext";
import ImageSelector from '../../../components/ImageSelectorFeatured';
import { ENDPOINT } from "../../../constants";
import { InputField } from "../../../components/InputField";
import { useApi } from "../../../contexts/ApiContext";
import { FetchApi } from "../../../api/fetch";

// Types
interface Message {
  recipient: string;
  text: string;
  image: string;
  actionType: 'redirect' | 'command';
}

interface ActionButton {
  id: number;
  text: string;
  value: string;
}

interface NewButton {
  text: string;
  value: string;
}

interface Command {
  value: string;
  label: string;
}

interface TelegramProps {
}

// Constants
const EXISTING_COMMANDS: Command[] = [
  { value: '/news', label: '/news - Get latest news' },
  { value: '/spaces', label: '/spaces - View spaces' },
  { value: '/mints', label: '/mints - View mint opportunities' },
  { value: '/airdrops', label: '/airdrops - View available airdrops' },
  { value: '/quests', label: '/quests - View available quests' },
  { value: '/lending', label: '/lending - View lending options' },
  { value: '/allpools', label: '/allpools - View all pools' },
  { value: '/sol', label: '/sol - SOL information' },
  { value: '/lst', label: '/lst - Liquid staking tokens' },
  { value: '/stablecoins', label: '/stablecoins - Stablecoin information' },
  { value: '/bounties', label: '/bounties - View bounties' },
  { value: '/topnfts', label: '/topnfts - Top NFT collections' },
  { value: '/topvolumesol', label: '/topvolumesol - Top volume Solana NFTs' },
  { value: '/toptrendingsol', label: '/toptrendingsol - Top trending Solana NFTs' },
  { value: '/topgainerssol', label: '/topgainerssol - Top gainers Solana NFTs' },
  { value: '/topmktcapsol', label: '/topmktcapsol - Top market cap Solana NFTs' },
  { value: '/staking', label: '/staking - Staking information' },
  { value: '/liquidstaking', label: '/liquidstaking - Liquid staking options' },
  { value: '/nativestaking', label: '/nativestaking - Native staking options' },
];

const RECIPIENT_OPTIONS = [
  { value: "0", label: "Select Recipient" },
  { value: "devs", label: "Testers And Devs" },
  { value: "chats", label: "All Active Chats" },
  { value: "profiles", label: "All Kyzzen Profiles" },
  { value: "wl_registrants", label: "WL Registrants" },
  { value: "wl_winners", label: "WL Winners" },
  { value: "wl_losers", label: "WL Non-Winners" },
  { value: "wl_owners", label: "KNS NFT Owners" },
  { value: "hope", label: "Hope" },
];

const MAX_MESSAGE_LENGTH = 4096;
const urlBase = "https://creator-portal-bucket.s3.us-east-2.amazonaws.com/"

const TelegramMessage: React.FC<TelegramProps> = React.memo(() => {
  const { getUserJWT, setIsLoadingOverlayDisplay } = useAuth();
  const history = useHistory()
  const { uploadFile } = useApi()

  // State
  const [logoFile, setLogoFile] = useState<File | undefined>(undefined);
  const [message, setMessage] = useState<Message>({
    recipient: "",
    text: "",
    image: "",
    actionType: "redirect"
  });
  const [actionButtons, setActionButtons] = useState<ActionButton[]>([]);
  const [showAddButton, setShowAddButton] = useState<boolean>(false);
  const [newButton, setNewButton] = useState<NewButton>({
    text: '',
    value: ''
  });

  // Utility function to count letters excluding HTML tags
  const countLetters = useCallback((str: string): number => {
    const regex = /<[^>]*>|[a-zA-Z0-9]/g;
    const matches = str.match(regex);
    const filteredChars = matches?.filter((match: string) =>
      !match.startsWith('<') || !match.endsWith('>')
    );
    return filteredChars?.length ?? 0;
  }, []);

  // Action button handlers
  const addActionButton = useCallback(() => {
    if (newButton.text.trim() && newButton.value.trim()) {
      const buttonToAdd: ActionButton = {
        ...newButton,
        id: Date.now(),
        text: newButton.text.trim(),
        value: newButton.value.trim()
      };

      setActionButtons(prev => [...prev, buttonToAdd]);
      setNewButton({ text: '', value: '' });
      setShowAddButton(false);
    }
  }, [newButton]);

  const removeActionButton = useCallback((id: number) => {
    setActionButtons(prev => prev.filter(button => button.id !== id));
  }, []);

  const handleNewButtonChange = useCallback((updates: Partial<NewButton>) => {
    setNewButton(prev => ({ ...prev, ...updates }));
  }, []);


  const cancelAddButton = useCallback(() => {
    setShowAddButton(false);
    setNewButton({ text: '', value: '' });
  }, []);

  // Image handling
  const onSelectCollectionThumbnail = useCallback(() => {
    setTimeout(() => {
      document?.getElementById("tgImageSelector")?.click();
    }, 0);
  }, []);

  const handleCollectionThumbnailSelected = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setMessage(prev => ({ ...prev, image: reader.result as string }));
      };
      reader.readAsDataURL(file);
    }
  }, []);

  // Form handlers
  const handleMessageChange = useCallback((updates: Partial<Message>) => {
    setMessage(prev => ({ ...prev, ...updates }));
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!message.recipient || !message.text.trim()) {
      return;
    }

    setIsLoadingOverlayDisplay(true);

    try {
      const JWT = await getUserJWT();

      // Convert logo file to base64 if exists
      let imageBase64 = "";
      const filename = urlBase + "telegram/" + logoFile?.name
      if (logoFile) {
        await uploadFile(logoFile, "telegram/" + logoFile?.name)
        imageBase64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64String = reader.result?.toString().split(',')[1]; // remove "data:image/...;base64,"
            resolve(base64String || "");
          };
          reader.onerror = reject;
          reader.readAsDataURL(logoFile);
        });
      }

      // Create payload
      const payload = {
        text: message.text,
        recipient: message.recipient,
        actionType: message.actionType,
        attachment: logoFile ? filename : undefined,
        actions: actionButtons.length > 0 ? actionButtons.map((a) => {
          return {
            text: a.text,
            value: a.value
          }
        }) : undefined,
        imageBase64: imageBase64 || undefined,
      };

      await FetchApi({
        endpoint: `${ENDPOINT.BASE_PROXY}/telegram-message`,
        method: "POST",
        JWT,
        body:  (payload),
      });

      history.push("/dashboard/telegram")

    } catch (error) {
      console.error('Error sending telegram message:', error);
    } finally {
      setIsLoadingOverlayDisplay(false);
    }

  };

  const downloadAttendance = async () => {
    setIsLoadingOverlayDisplay(true);

    try {
      const userJWT = await getUserJWT();
      const blob = await FetchApi({
        endpoint: `${ENDPOINT.BASE_ENDPOINT_TELEGRAM}/get-stats`,
        method: "POST",
        JWT: userJWT,
        format: 'blob'
      });

      const url = window.URL.createObjectURL(blob);
      window.location.assign(url);
    } catch (error) {
      console.error('Error downloading stats:', error);
    } finally {
      setIsLoadingOverlayDisplay(false);
    }
  };

  const isFormValid = message.recipient && message.text.trim();
  const isNewButtonValid = newButton.text.trim() && newButton.value.trim();

  return (
    <div className="flex flex-col w-11/12 mx-auto pb-2">
      {/* Header */}
      <div className="mt-8 flex flex-row items-center justify-between flex-col sm:flex-row">
        <div className="w-auto">
          <h1 className="font-semibold text-3xl text-left mt-8 mb-8">
            Send Telegram Message
          </h1>
        </div>
        <button
          onClick={downloadAttendance}
          className="btn bg-gradient text-kyzzen-primary block w-fit"
          type="button"
        >
          Download Stats
        </button>
      </div>

      {/* Main Form */}
      <form onSubmit={handleSubmit} className="flex flex-auto flex-col items-center w-full">
        {/* Recipient Selection */}
        <div className="mb-6 mt-12 w-full">
          <select
            name="recipient"
            className="w-full h-12 bg-kyzzen-primary text-gray-200 border border-2 border-kyzzen-secondary border-box px-2 rounded-lg"
            onChange={(e) => handleMessageChange({ recipient: e.target.value })}
            value={message.recipient}
            required
          >
            {RECIPIENT_OPTIONS.map((option) => (
              <option
                key={option.value}
                className="bg-black text-sm text-gray-200"
                style={{ color: "white" }}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Message Text */}
        <div className="mb-6 w-full">
          <label className="flex justify-between text-sm font-base text-gray-300 mb-3 w-full mr-0">
            Message Text
            <span>
              {countLetters(message.text)} / {MAX_MESSAGE_LENGTH}
            </span>
          </label>

          <div className="block bg-transparent border border-2 border-kyzzen-secondary text-black rounded-lg w-full rounded-lg overflow-hidden">
            <CKEditor
              editor={ClassicEditor}
              data={message.text}
              config={{
                toolbar: ['bold', 'italic', 'link', '|', 'undo', 'redo'],
                removePlugins: [
                  'Table', 'MediaEmbed', 'BlockQuote', 'List',
                  'Heading', 'Indent', 'Outdent'
                ]
              }}
              onChange={(_, editor) => {
                const data = editor.getData();
                handleMessageChange({ text: data });
              }}
            />
          </div>
        </div>
        {/* Action Type Selection */}
        <div className="mb-6 mt-12 w-full">
          <label className="flex justify-between text-sm font-base text-gray-300 mb-3 w-full mr-0">
            Action Type
          </label>
          <select
            name="actionType"
            className="w-full h-12 bg-kyzzen-primary text-gray-200 border border-2 border-kyzzen-secondary border-box px-2 rounded-lg"
            onChange={(e) => handleMessageChange({ actionType: e.target.value as 'redirect' | 'command' })}
            value={message.actionType}
            required
          >
            <option
              key={'redirect'}
              className="bg-black text-sm text-gray-200"
              style={{ color: "white" }}
              value={'redirect'}
            >
              Redirect
            </option>
            <option
              key={'command'}
              className="bg-black text-sm text-gray-200"
              style={{ color: "white" }}
              value={'command'}
            >
              Command
            </option>
          </select>
        </div>
        {/* Action Buttons Section */}
        <div className="mb-6 w-full">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-300">Action Buttons</h3>
            <button
              type="button"
              onClick={() => setShowAddButton(true)}
              className="flex items-center gap-2 btn bg-gradient px-4 py-2 rounded-md text-sm  text-kyzzen-primary "
            >
              <PlusIcon className="w-4 h-4" />
              Add Action Button
            </button>
          </div>

          {/* Existing Action Buttons */}
          {actionButtons.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              {actionButtons.map((button) => (
                <div key={button.id} className="bg-kyzzen-secondary rounded-lg p-4 border border-gray-700">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      {message.actionType === 'redirect' ? (
                        <ExternalLinkIcon className="w-4 h-4 text-blue-400" />
                      ) : (
                        <KeyboardCommandKey className="text-green-400" style={{ fontSize: '16px' }} />
                      )}
                      <span className="font-medium text-white">{button.text}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeActionButton(button.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="text-sm text-gray-400">
                    {message.actionType === 'redirect' ? `URL: ${button.value}` : `Command: ${button.value}`}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add New Action Button Form */}
          {showAddButton && (
            <div className="bg-kyzzen-primary rounded-lg p-6 border border-2 border-kyzzen-secondary mb-4">
              <h4 className="text-lg font-medium mb-4 text-white">Add New Action Button</h4>

              {/* Button Text */}
              <div className="mb-4">
                <InputField
                  label="Button Text"
                  type="text"
                  value={newButton.text}
                  onChange={(e) => handleNewButtonChange({ text: e.target.value })}
                  // className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter button text..."
                />
              </div>


              {/* Action Value */}
              <div className="mb-4">
                {message.actionType === 'redirect' ? (
                  <div>
                    <InputField
                      label="URL"
                      type="url"
                      value={newButton.value}
                      onChange={(e) => handleNewButtonChange({ value: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="https://example.com"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Enter the URL to redirect users to when they click this button
                    </p>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm  mb-2 text-gray-300">Select Command</label>
                    <select
                      value={newButton.value}
                      onChange={(e) => handleNewButtonChange({ value: e.target.value })}
                      className="w-full h-12 bg-kyzzen-primary text-gray-200 border border-2 border-kyzzen-secondary border-box px-2 rounded-lg"
                    >
                      <option value="">Choose existing command...</option>
                      {EXISTING_COMMANDS.map((cmd) => (
                        <option key={cmd.value} value={cmd.value}>
                          {cmd.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-400 mt-1">
                      Select an existing bot command to execute when users click this button
                    </p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={addActionButton}
                  disabled={!isNewButtonValid}
                  className="btn bg-gradient disabled:bg-gray-600 disabled:cursor-not-allowed px-4 py-2 rounded-lg text-sm font-medium text-kyzzen-secondary"
                >
                  Add Button
                </button>
                <button
                  type="button"
                  onClick={cancelAddButton}
                  className="bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded-lg text-sm font-medium text-white"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Image Upload */}
        <div className="w-full mb-6">
          <label className="flex justify-between text-sm font-base text-gray-300 mb-3 w-full mr-0">
            Image
          </label>
          <input
            style={{ display: "none" }}
            type="file"
            accept=".png, .jpg, .jpeg, .gif"
            id="tgImageSelector"
            onChange={handleCollectionThumbnailSelected}
          />
          <ImageSelector
            iconSrc={message.image}
            onSelectIcon={onSelectCollectionThumbnail}
          />
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!isFormValid}
          className="btn bg-gradient text-kyzzen-primary block w-full my-24 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Send Message
        </button>
      </form>
    </div>
  );
});

TelegramMessage.displayName = 'TelegramMessage';

export default TelegramMessage;